import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Plus, Trash2, Edit, X, Megaphone, Eye, EyeOff } from "lucide-react";

const empty = {
  message_ro: "",
  message_en: "",
  cta_label_ro: "",
  cta_label_en: "",
  cta_href: "",
  expires_at: "",
  enabled: true,
  bg_color: "#D4AF37",
  text_color: "#0A0A0A",
  sort_order: 0,
};

export default function AdminBanners() {
  const [banners, setBanners] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/admin/promo-banners");
      setBanners(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      console.error(e);
    }
  };
  useEffect(() => { load(); }, []);

  const openForm = (b) => {
    setEditingId(b?.id || null);
    setForm(
      b
        ? {
            message_ro: b.message_ro || "",
            message_en: b.message_en || "",
            cta_label_ro: b.cta_label_ro || "",
            cta_label_en: b.cta_label_en || "",
            cta_href: b.cta_href || "",
            expires_at: b.expires_at || "",
            enabled: !!b.enabled,
            bg_color: b.bg_color || "#D4AF37",
            text_color: b.text_color || "#0A0A0A",
            sort_order: b.sort_order ?? 0,
          }
        : empty
    );
    setError("");
    setOpen(true);
  };

  const close = () => { setOpen(false); setEditingId(null); setForm(empty); setError(""); };

  const save = async () => {
    setError("");
    if (!form.message_ro?.trim() || !form.message_en?.trim()) {
      setError("Mesajul RO si EN sunt obligatorii");
      return;
    }
    try {
      const payload = {
        ...form,
        expires_at: form.expires_at || null,
        sort_order: Number(form.sort_order) || 0,
      };
      if (editingId) await api.put(`/admin/promo-banners/${editingId}`, payload);
      else await api.post("/admin/promo-banners", payload);
      await load();
      close();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const toggleEnabled = async (b) => {
    try {
      await api.put(`/admin/promo-banners/${b.id}`, {
        message_ro: b.message_ro,
        message_en: b.message_en,
        cta_label_ro: b.cta_label_ro || "",
        cta_label_en: b.cta_label_en || "",
        cta_href: b.cta_href || "",
        expires_at: b.expires_at || null,
        enabled: !b.enabled,
        bg_color: b.bg_color || "#D4AF37",
        text_color: b.text_color || "#0A0A0A",
        sort_order: b.sort_order ?? 0,
      });
      await load();
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Stergi acest banner?")) return;
    await api.delete(`/admin/promo-banners/${id}`);
    await load();
  };

  return (
    <div data-testid="admin-banners">
      <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2" style={{ color: "var(--brand-primary)" }}>Dashboard</div>
          <h1 className="font-serif text-3xl sm:text-4xl flex items-center gap-3">
            <Megaphone className="w-7 h-7" style={{ color: "var(--brand-primary)" }} />
            Bannere promotionale
          </h1>
          <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--brand-text-secondary)" }}>
            Bannerele active vor fi rotite automat sus pe paginile publice (la fiecare 6 secunde). Daca dai &quot;expires_at&quot;, bannerul dispare automat dupa acea data.
          </p>
        </div>
        <button onClick={() => openForm(null)} className="btn-gold-filled flex items-center gap-2" data-testid="add-banner-btn">
          <Plus className="w-4 h-4" /> Banner nou
        </button>
      </div>

      <div className="card-surface overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b" style={{ borderColor: "var(--brand-border)" }}>
              <th className="text-left p-4 label-eyebrow">Mesaj RO</th>
              <th className="text-left p-4 label-eyebrow">Expira</th>
              <th className="text-left p-4 label-eyebrow">Ordine</th>
              <th className="text-left p-4 label-eyebrow">Preview</th>
              <th className="text-left p-4 label-eyebrow">Status</th>
              <th className="text-right p-4 label-eyebrow">Actiuni</th>
            </tr>
          </thead>
          <tbody>
            {banners.map((b) => (
              <tr key={b.id} className="border-b" style={{ borderColor: "var(--brand-border)" }} data-testid={`banner-${b.id}`}>
                <td className="p-4 max-w-md truncate">{b.message_ro}</td>
                <td className="p-4" style={{ color: "var(--brand-text-secondary)" }}>{b.expires_at || "—"}</td>
                <td className="p-4">{b.sort_order ?? 0}</td>
                <td className="p-4">
                  <span className="inline-block px-2 py-1 text-xs rounded" style={{ background: b.bg_color, color: b.text_color }}>
                    aA
                  </span>
                </td>
                <td className="p-4">
                  <button onClick={() => toggleEnabled(b)} className="btn-ghost flex items-center gap-1 text-xs" data-testid={`toggle-banner-${b.id}`}>
                    {b.enabled ? <><Eye className="w-3.5 h-3.5" /> Activ</> : <><EyeOff className="w-3.5 h-3.5" /> Inactiv</>}
                  </button>
                </td>
                <td className="p-4 text-right">
                  <button onClick={() => openForm(b)} className="btn-ghost mr-2" data-testid={`edit-banner-${b.id}`}><Edit className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(b.id)} className="btn-ghost" data-testid={`delete-banner-${b.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
            {banners.length === 0 && (
              <tr><td colSpan={6} className="text-center py-10 text-sm" style={{ color: "var(--brand-text-secondary)" }}>Niciun banner. Adauga unul.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 sm:p-6" onClick={close}>
          <div className="card-surface p-5 sm:p-8 w-full max-w-2xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl">{editingId ? "Editeaza banner" : "Banner nou"}</h2>
              <button onClick={close} data-testid="close-banner-form"><X className="w-5 h-5" /></button>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <Field label="Mesaj RO *">
                <input className="brand-input" value={form.message_ro} placeholder="20% reducere pana duminica!" onChange={(e) => setForm({...form, message_ro: e.target.value})} data-testid="banner-msg-ro" />
              </Field>
              <Field label="Mesaj EN *">
                <input className="brand-input" value={form.message_en} placeholder="20% off until Sunday!" onChange={(e) => setForm({...form, message_en: e.target.value})} data-testid="banner-msg-en" />
              </Field>
              <Field label="CTA text RO">
                <input className="brand-input" value={form.cta_label_ro} placeholder="Rezerva acum" onChange={(e) => setForm({...form, cta_label_ro: e.target.value})} data-testid="banner-cta-ro" />
              </Field>
              <Field label="CTA text EN">
                <input className="brand-input" value={form.cta_label_en} placeholder="Book now" onChange={(e) => setForm({...form, cta_label_en: e.target.value})} data-testid="banner-cta-en" />
              </Field>
              <Field label="CTA link (ex. /oferte sau #fleet)">
                <input className="brand-input" value={form.cta_href} placeholder="/oferte" onChange={(e) => setForm({...form, cta_href: e.target.value})} data-testid="banner-cta-href" />
              </Field>
              <Field label="Expira la (optional)">
                <input type="date" className="brand-input" value={form.expires_at} onChange={(e) => setForm({...form, expires_at: e.target.value})} data-testid="banner-expires" />
              </Field>
              <Field label="Ordine afisare (mai mic = mai intai)">
                <input type="number" className="brand-input" value={form.sort_order} onChange={(e) => setForm({...form, sort_order: e.target.value})} data-testid="banner-sort" />
              </Field>
              <div />
              <Field label="Culoare fundal">
                <div className="flex items-center gap-2">
                  <input type="color" className="brand-input p-1 w-16 h-10" value={form.bg_color} onChange={(e) => setForm({...form, bg_color: e.target.value})} data-testid="banner-bg" />
                  <input className="brand-input flex-1" value={form.bg_color} onChange={(e) => setForm({...form, bg_color: e.target.value})} />
                </div>
              </Field>
              <Field label="Culoare text">
                <div className="flex items-center gap-2">
                  <input type="color" className="brand-input p-1 w-16 h-10" value={form.text_color} onChange={(e) => setForm({...form, text_color: e.target.value})} data-testid="banner-text-color" />
                  <input className="brand-input flex-1" value={form.text_color} onChange={(e) => setForm({...form, text_color: e.target.value})} />
                </div>
              </Field>
            </div>

            <label className="flex items-center gap-2 mb-4 text-sm">
              <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({...form, enabled: e.target.checked})} data-testid="banner-enabled" />
              Activ (vizibil pe site)
            </label>

            {/* Live preview */}
            <div className="label-eyebrow mb-2">Preview</div>
            <div className="px-4 py-2.5 text-center text-sm rounded mb-5" style={{ background: form.bg_color, color: form.text_color }} data-testid="banner-preview">
              <b>{form.message_ro || "(mesaj RO)"}</b>
              {form.cta_label_ro && form.cta_href && (
                <span className="ml-3 underline">{form.cta_label_ro} →</span>
              )}
            </div>

            {error && <div className="text-sm mb-3" style={{ color: "var(--brand-primary)" }}>{error}</div>}

            <div className="flex gap-3">
              <button onClick={save} className="btn-gold-filled flex-1" data-testid="save-banner-btn">Salveaza</button>
              <button onClick={close} className="btn-ghost">Anuleaza</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label-eyebrow block mb-2">{label}</label>
      {children}
    </div>
  );
}
